import { Tab } from "@headlessui/react";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

import Header from "~/components/Header";
import { HOMEPAGE } from "~/lib/homepage";

const features = [
	{
		title: `4 Certifications`,
		description: HOMEPAGE.whatWeOfferCertificationsLine,
		image: `${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/Screenshot%202024-11-20%20at%205.34.24%E2%80%AFAM.png`,
	},
	{
		title: `200+ Courses`,
		description: `Pre-approved for continuing education credits (CECs) (accreditations) and our 3rd-party approved certifications.`,
		image: `${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/courses-screenshot.png`,
	},
	{
		title: `500+ Videos`,
		description: `Practical video on fitness, sports performance, physical rehabilitation, and sports medicine.`,
		image: `${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/videos-screenshot.png`,
	},
	{
		title: `500+ Articles`,
		description: `Search from a huge library of articles covering including the latest research, myths, common questions, and practical applications. `,
		image: `${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/articles-screenshot.png`,
	},
	{
		title: `Live Webinars`,
		description: `Members get free access to live webinars.`,
		image: `${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/Screenshot%202024-06-13%20at%204.50.14%E2%80%AFAM.png`,
	},
	{
		title: `AI Tutor`,
		description: `Unlock your learning potential with the Brookbush Institute AI Tutor.`,
		image: `${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/Screenshot%202024-11-20%20at%205.45.19%E2%80%AFAM.png`,
	},
	{
		title: HOMEPAGE.nccaExamCounterTitle,
		description: HOMEPAGE.nccaExamCounterDescription,
		image: `${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/Screenshot%202024-11-20%20at%205.34.24%E2%80%AFAM.png`,
		href: HOMEPAGE.nccaExamCounterHref,
	},
];

const HomeExplorer = () => {
	const [tabOrientation, setTabOrientation] = useState(`horizontal`);

	useEffect(() => {
		const lgMediaQuery = window.matchMedia(`(min-width: 1024px)`);

		function onMediaQueryChange({ matches }: any) {
			setTabOrientation(matches ? `vertical` : `horizontal`);
		}

		onMediaQueryChange(lgMediaQuery);
		lgMediaQuery.addEventListener(`change`, onMediaQueryChange);

		return () => {
			lgMediaQuery.removeEventListener(`change`, onMediaQueryChange);
		};
	}, []);

	return (
		<section
			id="features"
			aria-labelledby="primary-features-title"
			className="relative z-0 overflow-hidden bg-slate-50 px-6 py-12 text-center sm:px-7 sm:py-16 lg:pr-0 lg:pl-16 lg:text-left xl:pt-18 xl:pb-24 2xl:px-0 dark:bg-slate-700"
		>
			<div className="relative mx-auto flex max-w-screen-2xl flex-col lg:gap-12 xl:gap-16">
				<Header
					id="primary-features-title"
					title="What we offer"
					description="'It's Amazing... Like Netflix for certifications and courses!'- Jen B."
				/>
				<Tab.Group
					as="div"
					className="grid grid-cols-1 items-center gap-y-2 pt-6 sm:gap-y-6 sm:pt-10 lg:grid-cols-12 lg:pt-0"
					vertical={tabOrientation === `vertical`}
				>
					{({ selectedIndex }) => (
						<>
							<div className="flex overflow-x-auto pb-4 sm:mx-0 sm:overflow-visible sm:pb-0 lg:col-span-5">
								<Tab.List className="relative z-10 flex gap-x-4 whitespace-nowrap sm:mx-auto sm:px-0 lg:mx-0 lg:block lg:gap-x-0 lg:gap-y-1 lg:whitespace-normal">
									{features.map((feature, featureIndex) => (
										<div
											key={feature.title}
											className={clsx(
												`group relative rounded-full border border-transparent border-r-0 px-4 py-1 duration-500 lg:rounded-r-none lg:rounded-l-xl lg:p-6`,
												selectedIndex === featureIndex
													? `bg-white shadow-slate-200/30 shadow-xl dark:bg-slate-600 dark:shadow-slate-900/10`
													: `hover:bg-white/10 lg:hover:bg-slate-200/20 dark:lg:hover:bg-slate-800/10`,
											)}
										>
											<h3>
												<Tab
													className={clsx(
														`font-medium text-lg lg:text-xl [&:not(:focus-visible)]:focus:outline-none`,
													)}
												>
													<span className="absolute inset-0 rounded-full lg:rounded-r-none lg:rounded-l-xl" />
													{feature.title}
												</Tab>
											</h3>
											<p className={clsx(`mt-2 hidden lg:block`)}>
												{feature.description}
												{"href" in feature && feature.href ? (
													<>
														{" "}
														<Link
															href={feature.href}
															className="relative z-20 font-medium text-sky-700 hover:underline dark:text-sky-300"
															onClick={(e) => e.stopPropagation()}
														>
															Exam portal →
														</Link>
													</>
												) : null}
											</p>
										</div>
									))}
								</Tab.List>
							</div>
							<Tab.Panels className="lg:col-span-7">
								{features.map((feature) => (
									<Tab.Panel key={feature.title} unmount={false}>
										<div className="relative sm:px-6 lg:hidden">
											<p className="relative mx-auto max-w-2xl text-base text-slate-700 sm:text-center">
												{feature.description}
												{"href" in feature && feature.href ? (
													<>
														{" "}
														<Link
															href={feature.href}
															className="font-medium text-sky-700 hover:underline dark:text-sky-300"
														>
															Exam portal →
														</Link>
													</>
												) : null}
											</p>
										</div>

										<div
											className={clsx(
												`mt-10 w-[40rem] overflow-hidden rounded-2xl border shadow-slate-200/70 shadow-xl transition-all duration-1000 sm:w-auto lg:mt-0 lg:w-[67.8125rem] 2xl:w-full dark:border-none dark:shadow-slate-900/30`,
											)}
										>
											<Image
												src={feature.image}
												alt=""
												width={1000}
												height={1}
												quality={70}
												sizes="(min-width: 1024px) 67.8125rem, (min-width: 640px) 100vw, 45rem"
											/>
										</div>
									</Tab.Panel>
								))}
							</Tab.Panels>
						</>
					)}
				</Tab.Group>
			</div>
		</section>
	);
};

export default HomeExplorer;
